import { useEffect, useRef } from 'react';

interface NorthernLightsProps {
  isDarkMode: boolean;
}

const NorthernLights = ({ isDarkMode }: NorthernLightsProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    let animationId: number;
    let time = 0;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      time += 0.005;

      // Draw 3 aurora ribbons
      const ribbons = [
        { y: height * 0.15, amplitude: 60, frequency: 0.002, color: 'rgba(0, 255, 255, ', opacity: 0.15 },
        { y: height * 0.25, amplitude: 80, frequency: 0.0015, color: 'rgba(100, 255, 200, ', opacity: 0.12 },
        { y: height * 0.35, amplitude: 50, frequency: 0.0025, color: 'rgba(0, 200, 255, ', opacity: 0.1 },
      ];

      ribbons.forEach((ribbon, i) => {
        // Create vertical gradient for aurora
        const gradient = ctx.createLinearGradient(0, ribbon.y - ribbon.amplitude, 0, ribbon.y + ribbon.amplitude * 2);
        gradient.addColorStop(0, ribbon.color + '0)');
        gradient.addColorStop(0.3, ribbon.color + ribbon.opacity + ')');
        gradient.addColorStop(0.6, ribbon.color + (ribbon.opacity * 0.7) + ')');
        gradient.addColorStop(1, ribbon.color + '0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();

        // Top wavy line
        for (let x = 0; x <= width; x += 5) {
          const y = ribbon.y + Math.sin(x * ribbon.frequency + time + i) * ribbon.amplitude;
          if (x === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        // Bottom line (straight down for fill)
        ctx.lineTo(width, ribbon.y + ribbon.amplitude * 2);
        ctx.lineTo(0, ribbon.y + ribbon.amplitude * 2);
        ctx.closePath();
        ctx.fill();
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.3,
      }}
    />
  );
};

export default NorthernLights;
